<template>
  <sfs-form-modal
    :id="modalId"
    title="Update Billing Details"
    size="lg"
  >
    <b-form
      novalidate
      @submit.stop.prevent="handleSubmit"
    >
      <b-row>
        <b-col>
          <b-alert
            :show="hasBaseServerErrors()"
            variant="danger"
            dismissible
          >
            <p
              class="mb-0"
              v-for="(error, index) in baseServerErrors"
              :key="index"
            >
              {{ error }}
            </p>
          </b-alert>
        </b-col>
      </b-row>

      <b-row>
        <b-col lg="6">
          <b-form-group
            :label="fieldLabels.contact_name"
            label-for="client-contact_name"
          >
            <b-form-input
              id="client-contact_name"
              v-model="$v.form.contact_name.$model"
              autocomplete="new-password"
              :state="$v.form.contact_name.$dirty ? !$v.form.contact_name.$error : null"
              type="text"
            />
            <b-form-invalid-feedback
              v-if="$v.form.contact_name.$dirty"
              id="client-contact_name-feedback"
            >
              <span v-if="!$v.form.contact_name.maxLength">
                The billing contact name must be less than {{ $v.form.contact_name.$params.maxLength.max + 1 }} characters.
              </span>
              <span v-if="!$v.form.contact_name.serverFailed">{{ serverErrors.contact_name }}</span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <b-col lg="6">
          <b-form-group
            :label="fieldLabels.attention_to"
            label-for="client-attention_to"
          >
            <b-form-input
              id="client-attention_to"
              v-model="$v.form.attention_to.$model"
              :state="$v.form.attention_to.$dirty ? !$v.form.attention_to.$error : null"
              type="text"
              placeholder="ATTN: Accounts Payable"
            />
            <b-form-invalid-feedback
              v-if="$v.form.attention_to.$dirty"
              id="client-attention_to-feedback"
            >
              <span v-if="!$v.form.attention_to.maxLength">
                The attention to must be less than {{ $v.form.attention_to.$params.maxLength.max + 1 }} characters.
              </span>
              <span v-if="!$v.form.attention_to.serverFailed">{{ serverErrors.attention_to }}</span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col lg="6">
          <b-form-group
            :label="`${fieldLabels.address}${form.create_location ? ' *' : ''}`"
            label-for="client-address"
          >
            <b-form-input
              id="client-address"
              v-model="$v.form.address.$model"
              :state="$v.form.address.$dirty ? !$v.form.address.$error : null"
              type="text"
              placeholder="123 Fake Street"
            />
            <b-form-invalid-feedback
              v-if="$v.form.address.$dirty"
              id="client-address-feedback"
            >
              <span v-if="!$v.form.address.required">Please enter an address.</span>
              <span v-if="!$v.form.address.maxLength">
                The address must be less than {{ $v.form.address.$params.maxLength.max + 1 }} characters.
              </span>
              <span v-if="!$v.form.address.serverFailed">{{ serverErrors.address }}</span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <b-col lg="6">
          <b-form-group
            :label="`${fieldLabels.city}${form.create_location ? ' *' : ''}`"
            label-for="client-city"
          >
            <b-form-input
              id="client-city"
              v-model="$v.form.city.$model"
              :state="$v.form.city.$dirty ? !$v.form.city.$error : null"
              type="text"
              placeholder="New York"
            />
            <b-form-invalid-feedback
              v-if="$v.form.city.$dirty"
              id="client-city-feedback"
            >
              <span v-if="!$v.form.city.required">Please enter an city.</span>
              <span v-if="!$v.form.city.maxLength">
                The city must be less than {{ $v.form.city.$params.maxLength.max + 1 }} characters.
              </span>
              <span v-if="!$v.form.city.serverFailed">{{ serverErrors.city }}</span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col lg="6">
          <b-form-group
            :label="`${fieldLabels.state}${form.create_location ? ' *' : ''}`"
            label-for="client-state"
          >
            <b-form-input
              id="client-state"
              v-model="$v.form.state.$model"
              :state="$v.form.state.$dirty ? !$v.form.state.$error : null"
              type="text"
              placeholder="NY"
            />
            <b-form-invalid-feedback
              v-if="$v.form.state.$dirty"
              id="client-state-feedback"
            >
              <span v-if="!$v.form.state.required">Please enter a state.</span>
              <span v-if="!$v.form.state.maxLength">
                The state must be less than {{ $v.form.state.$params.maxLength.max + 1 }} characters.
              </span>
              <span v-if="!$v.form.state.serverFailed">{{ serverErrors.state }}</span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <b-col lg="6">
          <b-form-group
            :label="`${fieldLabels.zip_code}${form.create_location ? ' *' : ''}`"
            label-for="client-zip_code"
          >
            <b-form-input
              id="client-zip_code"
              v-model="$v.form.zip_code.$model"
              :state="$v.form.zip_code.$dirty ? !$v.form.zip_code.$error : null"
              type="text"
              placeholder="55555"
            />
            <b-form-invalid-feedback
              v-if="$v.form.zip_code.$dirty"
              id="client-zip_code-feedback"
            >
              <span v-if="!$v.form.zip_code.required">Please enter a zip code.</span>
              <span v-if="!$v.form.zip_code.maxLength">
                The zip code must be less than {{ $v.form.zip_code.$params.maxLength.max + 1 }} characters.
              </span>
              <span v-if="!$v.form.zip_code.serverFailed">{{ serverErrors.zip_code }}</span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>

      <hr class="mt-4">

      <b-row>
        <b-col>
          <b-row>
            <b-col>
              <b-form-group
                :label="fieldLabels.invoice_delivery_method"
                label-for="client-invoice_delivery_method"
              >
                <invoice-delivery-method-select
                  id="client-invoice_delivery_method"
                  class="mb-1"
                  :select-class="{ 'is-invalid': $v.form.invoice_delivery_method.$dirty && $v.form.invoice_delivery_method.$error }"
                  v-model="$v.form.invoice_delivery_method.$model"
                />
                <b-form-textarea
                  id="client-invoice_delivery_notes"
                  v-model="$v.form.invoice_delivery_notes.$model"
                  :state="$v.form.invoice_delivery_notes.$dirty ? !$v.form.invoice_delivery_notes.$error : null"
                  placeholder="Invoice Delivery Notes"
                  rows="3"
                />
                <b-form-invalid-feedback
                  v-if="$v.form.invoice_delivery_notes.$dirty"
                  id="client-invoice_delivery_notes-feedback"
                >
                  <span v-if="!$v.form.invoice_delivery_notes.serverFailed">{{ serverErrors.invoice_delivery_notes }}</span>
                </b-form-invalid-feedback>
                <b-form-invalid-feedback
                  v-if="$v.form.invoice_delivery_method.$dirty"
                  id="client-invoice_delivery_method-feedback"
                >
                  <span v-if="!$v.form.invoice_delivery_method.required">Please enter a delivery method.</span>
                  <span v-if="!$v.form.invoice_delivery_method.serverFailed">{{ serverErrors.invoice_delivery_method }}</span>
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>
        </b-col>

        <b-col>
          <b-row>
            <b-col cols="12">
              <b-form-group label-for="client-disable_auto_invoice_delivery">
                <b-form-checkbox
                  switch
                  id="client-disable_auto_invoice_delivery"
                  v-model="$v.form.disable_auto_invoice_delivery.$model"
                >
                  {{ fieldLabels.disable_auto_invoice_delivery }}
                </b-form-checkbox>
              </b-form-group>
            </b-col>

            <b-col cols="12">
              <b-form-group label-for="client-send_physical_invoice">
                <b-form-checkbox
                  switch
                  id="client-send_physical_invoice"
                  v-model="$v.form.send_physical_invoice.$model"
                >
                  {{ fieldLabels.send_physical_invoice }}
                </b-form-checkbox>
              </b-form-group>
            </b-col>

            <b-col cols="12">
              <b-form-group
                :label="fieldLabels.relationship"
                label-for="client-relationship"
              >
                <b-form-input
                  id="client-relationship"
                  v-model="$v.form.relationship.$model"
                  :state="$v.form.relationship.$dirty ? !$v.form.relationship.$error : null"
                  type="text"
                />
                <b-form-invalid-feedback
                  v-if="$v.form.relationship.$dirty"
                  id="client-relationship-feedback"
                >
                  <span v-if="!$v.form.relationship.maxLength">
                    The attention to must be less than {{ $v.form.relationship.$params.maxLength.max + 1 }} characters.
                  </span>
                  <span v-if="!$v.form.relationship.serverFailed">{{ serverErrors.relationship }}</span>
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>
        </b-col>
      </b-row>

      <b-row>
        <b-col lg="6">
          <b-form-group
            :label="fieldLabels.purchase_order_number"
            label-for="client-purchase_order_number"
          >
            <b-form-input
              id="client-purchase_order_number"
              v-model="$v.form.purchase_order_number.$model"
              autocomplete="new-password"
              :state="$v.form.purchase_order_number.$dirty ? !$v.form.purchase_order_number.$error : null"
              type="text"
            />
            <b-form-invalid-feedback
              v-if="$v.form.purchase_order_number.$dirty"
              id="client-purchase_order_number-feedback"
            >
              <span v-if="!$v.form.purchase_order_number.maxLength">
                The billing PO # must be less than {{ $v.form.purchase_order_number.$params.maxLength.max + 1 }} characters.
              </span>
              <span v-if="!$v.form.purchase_order_number.serverFailed">{{ serverErrors.purchase_order_number }}</span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>

      <hr class="mt-4">

      <b-row>
        <b-col lg="6">
          <b-form-group
            :label="fieldLabels.payment_term_days"
            label-for="client-payment_term_days"
          >
            <b-input-group
              prepend="NET"
              append="Days"
              :class="{ 'is-invalid': $v.form.payment_term_days.$dirty && $v.form.payment_term_days.$error }"
            >
              <b-form-input
                id="client-payment_term_days"
                v-model="$v.form.payment_term_days.$model"
                :state="$v.form.payment_term_days.$dirty ? !$v.form.payment_term_days.$error : null"
                type="number"
              />
            </b-input-group>
            <b-form-invalid-feedback
              v-if="$v.form.payment_term_days.$dirty"
              id="client-payment_term_days-feedback"
            >
              <span v-if="!$v.form.payment_term_days.required">Please enter the number of payment term days.</span>
              <span v-if="!$v.form.payment_term_days.integer">Please enter a number.</span>
              <span v-if="!$v.form.payment_term_days.minValue">Please enter a positive number.</span>
              <span v-if="!$v.form.payment_term_days.serverFailed">{{ serverErrors.payment_term_days }}</span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          lg="6"
        >
          <b-form-group
            :label="fieldLabels.invoice_format_id"
            label-for="client-invoice_format_id"
          >
            <invoice-formats-select
              id="client-invoice_format_id"
              :select-class="{ 'is-invalid': $v.form.invoice_format_id.$dirty && $v.form.invoice_format_id.$error }"
              :value="form.invoice_format"
              @input="onInvoiceFormatChange"
            />
            <b-form-invalid-feedback
              v-if="$v.form.invoice_format_id.$dirty"
              id="client-invoice_format_id-feedback"
            >
              <span v-if="!$v.form.invoice_format_id.required">Please enter an invoice format.</span>
              <span v-if="!$v.form.invoice_format_id.serverFailed">{{ serverErrors.invoice_format_id }}</span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <b-col lg="6">
          <b-form-group
            :label="fieldLabels.technician_payment_gl_code_id"
            label-for="client-technician_payment_gl_code_id"
          >
            <gl-code-select
              id="client-technician_payment_gl_code_id"
              :select-class="{ 'is-invalid': $v.form.technician_payment_gl_code_id.$dirty && $v.form.technician_payment_gl_code_id.$error }"
              client-unassigned
              :value="form.technician_payment_gl_code"
              @input="onTechnicianPaymentGlCodeChange"
              :filter-params="technicianPaymentGlCodeFilters"
            />
            <b-form-invalid-feedback
              v-if="$v.form.technician_payment_gl_code_id.$dirty"
              id="client-technician_payment_gl_code_id-feedback"
            >
              <span v-if="!$v.form.technician_payment_gl_code_id.serverFailed">
                {{ serverErrors.technician_payment_gl_code }}
              </span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <b-col lg="6">
          <b-form-group
            :label="fieldLabels.invoice_gl_code_id"
            label-for="client-invoice_gl_code_id"
          >
            <gl-code-select
              id="client-invoice_gl_code_id"
              :select-class="{ 'is-invalid': $v.form.invoice_gl_code_id.$dirty && $v.form.invoice_gl_code_id.$error }"
              client-unassigned
              :value="form.invoice_gl_code"
              @input="onInvoiceGlCodeChange"
              :filter-params="invoiceGlCodeFilters"
            />
            <b-form-invalid-feedback
              v-if="$v.form.invoice_gl_code_id.$dirty"
              id="client-invoice_gl_code_id-feedback"
            >
              <span v-if="!$v.form.invoice_gl_code_id.serverFailed">
                {{ serverErrors.invoice_gl_code }}
              </span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col lg="6">
          <b-form-group label-for="client-tax_exempt">
            <b-form-checkbox
              switch
              id="client-tax_exempt"
              v-model="$v.form.tax_exempt.$model"
            >
              {{ fieldLabels.tax_exempt }}
            </b-form-checkbox>
          </b-form-group>
        </b-col>

        <b-col lg="6">
          <b-form-group label-for="client-invoice_recurring_by_location">
            <b-form-checkbox
              switch
              id="client-invoice_recurring_by_location"
              v-model="$v.form.invoice_recurring_by_location.$model"
            >
              {{ fieldLabels.invoice_recurring_by_location }}
            </b-form-checkbox>
          </b-form-group>
        </b-col>

        <b-col
          offset-lg="6"
          lg="6"
        >
          <b-form-group label-for="client-invoice_work_orders_individually">
            <b-form-checkbox
              switch
              id="client-invoice_work_orders_individually"
              v-model="$v.form.invoice_work_orders_individually.$model"
            >
              {{ fieldLabels.invoice_work_orders_individually }}
            </b-form-checkbox>
          </b-form-group>
        </b-col>
      </b-row>

      <hr class="mt-4">

      <b-row>
        <b-col lg="6">
          <b-form-group
            :label="fieldLabels.supply_order_invoice_gl_code_id"
            label-for="client-supply_order_invoice_gl_code_id"
          >
            <gl-code-select
              id="client-supply_order_invoice_gl_code_id"
              :select-class="{ 'is-invalid': $v.form.supply_order_invoice_gl_code_id.$dirty && $v.form.supply_order_invoice_gl_code_id.$error }"
              client-unassigned
              :value="form.supply_order_invoice_gl_code"
              @input="onSupplyOrderInvoiceGlCodeChange"
              :filter-params="invoiceGlCodeFilters"
            />
            <b-form-invalid-feedback
              v-if="$v.form.supply_order_invoice_gl_code_id.$dirty"
              id="client-supply_order_invoice_gl_code_id-feedback"
            >
              <span v-if="!$v.form.supply_order_invoice_gl_code_id.serverFailed">
                {{ serverErrors.supply_order_invoice_gl_code }}
              </span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <b-col lg="6">
          <b-form-group
            :label="fieldLabels.supply_order_default_markup_percent"
            label-for="client-supply_order_default_markup_percent"
          >
            <b-input-group
              append="%"
              :class="{ 'is-invalid': $v.form.supply_order_default_markup_percent.$dirty && $v.form.supply_order_default_markup_percent.$error }"
            >
              <b-form-input
                name="client-supply_order_default_markup_percent"
                v-model="$v.form.supply_order_default_markup_percent.$model"
                :state="$v.form.supply_order_default_markup_percent.$dirty ? !$v.form.supply_order_default_markup_percent.$error : null"
                type="text"
              />
            </b-input-group>
            <b-form-invalid-feedback
              v-if="$v.form.supply_order_default_markup_percent.$dirty"
              class="client-supply_order_default_markup_percent-feedback"
            >
              <span v-if="!$v.form.supply_order_default_markup_percent.integer">Please enter a number.</span>
              <span v-if="!$v.form.supply_order_default_markup_percent.between">Must be between {{ $v.form.supply_order_default_markup_percent.$params.between.min }} and {{ $v.form.supply_order_default_markup_percent.$params.between.max }}</span>
              <span v-if="!$v.form.supply_order_default_markup_percent.serverFailed">{{ serverErrors.supply_order_default_markup }}</span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <b-col lg="6">
          <b-form-group
            :label="fieldLabels.netsuite_department_id"
            label-for="client-netsuite_department_id"
          >
            <net-suite-department-record-select
              name="client-netsuite_department_id"
              :value="form.netsuite_department_id"
              @input="onNetSuiteDepartmentChange"
              :select-class="{ 'is-invalid': $v.form.netsuite_department_id.$dirty && $v.form.netsuite_department_id.$error }"
            />
            <b-form-invalid-feedback
              v-if="$v.form.netsuite_department_id.$dirty"
              class="client-netsuite_department_id-feedback"
            >
              <span v-if="!$v.form.netsuite_department_id.serverFailed">{{ serverErrors.netsuite_department_id }}</span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>


      <hr class="mt-4">

      <b-row>
        <b-col
          offset-lg="6"
          lg="3"
          order="2"
          order-lg="1"
        >
          <b-button
            variant="link-dark"
            block
            id="client-settings-close-button"
            @click="closeModal"
          >
            Cancel
          </b-button>
        </b-col>
        <b-col
          lg="3"
          order="1"
          order-lg="2"
        >
          <b-button
            id="client-settings-submit-button"
            block
            type="submit"
            variant="primary"
            :disabled="processing"
          >
            Save
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </sfs-form-modal>
</template>

<script>
import { required, requiredIf, maxLength, minValue, integer, decimal, between } from 'vuelidate/lib/validators';
import formMixin from '@/mixins/formMixin';
import formModalMixin from '@/mixins/formModalMixin';
import GlCodeSelect from '@/components/admin/glCodes/Select.vue';
import InvoiceFormatsSelect from '@/components/admin/invoiceFormats/Select.vue';
import InvoiceDeliveryMethodSelect from '@/components/admin/clients/InvoiceDeliveryMethodSelect.vue';
import NetSuiteDepartmentRecordSelect from '@/components/admin/integrationReferences/NetSuiteDepartmentRecordSelect.vue';
import { GL_CODE_TECHNICIAN_PAYMENT_CATEGORY, GL_CODE_INVOICE_CATEGORY } from '@/constants/glCodes';

export default {
  name: 'ClientsBillingDetailsModal',
  components: {
    GlCodeSelect,
    InvoiceFormatsSelect,
    InvoiceDeliveryMethodSelect,
    NetSuiteDepartmentRecordSelect,
  },
  mixins: [formMixin, formModalMixin],
  props: {
    value: Object,
  },
  data() {
    return {
      modalId: 'client-billing-details-modal',
      form: {
        create_location: this.value.create_location,
        relationship: this.value.relationship,
        contact_name: this.value.contact_name,
        attention_to: this.value.attention_to,
        address: this.value.address,
        city: this.value.city,
        state: this.value.state,
        zip_code: this.value.zip_code,
        disable_auto_invoice_delivery: this.value.disable_auto_invoice_delivery,
        send_physical_invoice: this.value.send_physical_invoice,
        invoice_delivery_method: this.value.invoice_delivery_method,
        invoice_delivery_notes: this.value.invoice_delivery_notes,
        payment_term_days: this.value.payment_term_days,
        invoice_format_id: this.value.invoice_format_id,
        invoice_format: this.value.invoice_format,
        technician_payment_gl_code_id: this.value.technician_payment_gl_code_id,
        technician_payment_gl_code: this.value.technician_payment_gl_code,
        invoice_gl_code_id: this.value.invoice_gl_code_id,
        invoice_gl_code: this.value.invoice_gl_code,
        supply_order_invoice_gl_code_id: this.value.supply_order_invoice_gl_code_id,
        supply_order_invoice_gl_code: this.value.supply_order_invoice_gl_code,
        purchase_order_number: this.value.purchase_order_number,
        netsuite_department_id: this.value.netsuite_department_id,
        supply_order_default_markup: this.value.supply_order_default_markup,
        supply_order_default_markup_percent: this.value.supply_order_default_markup ? this.value.supply_order_default_markup * 100 : null,
        tax_exempt: this.value.tax_exempt,
        invoice_recurring_by_location: this.value.invoice_recurring_by_location,
        invoice_work_orders_individually: this.value.invoice_work_orders_individually,
      },
      fieldLabels: {
        contact_name: 'Contact Name',
        relationship: 'Relationship',
        attention_to: 'Attention To',
        address: 'Address',
        city: 'City',
        state: 'State',
        zip_code: 'Zip Code',
        disable_auto_invoice_delivery: 'Disable automatic sending of invoices?',
        send_physical_invoice: `${this.$store.getters.translate('client', 'Client')} requires a physical invoice?`,
        invoice_delivery_method: `How to deliver ${this.$store.getters.translate('client', 'Client')} invoices?`,
        invoice_delivery_notes: 'Invoice Delivery Notes',
        purchase_order_number: 'Billing PO #',
        payment_term_days: 'Payment Terms',
        invoice_format_id: 'Invoice Format *',
        technician_payment_gl_code_id: 'Default Technician Payment GL Code',
        invoice_gl_code_id: 'Default Invoice GL Code',
        supply_order_invoice_gl_code_id: 'Supply Order Invoice GL Code',
        netsuite_department_id: 'NetSuite Department',
        supply_order_default_markup_percent: 'Supply Order Default Markup',
        tax_exempt: `${this.$store.getters.translate('client', 'Client')} is exempt from paying taxes?`,
        invoice_recurring_by_location: 'Generate recurring invoices by location?',
        invoice_work_orders_individually: 'Generate invoices for work orders individually?',
      },
      technicianPaymentGlCodeFilters: { client_id: this.value.id, category: GL_CODE_TECHNICIAN_PAYMENT_CATEGORY, active: true },
      invoiceGlCodeFilters: { client_id: this.value.id, category: GL_CODE_INVOICE_CATEGORY, active: true },
    };
  },
  validations: {
    form: {
      create_location: {},
      contact_name: {
        maxLength: maxLength(255),
        serverFailed() {
          return !this.hasServerErrors('contact_name');
        }
      },
      relationship: {
        maxLength: maxLength(255),
        serverFailed() {
          return !this.hasServerErrors('relationship');
        },
      },
      attention_to: {
        maxLength: maxLength(255),
        serverFailed() {
          return !this.hasServerErrors('attention_to');
        },
      },
      disable_auto_invoice_delivery: {},
      send_physical_invoice: {},
      invoice_delivery_method: {
        required,
        serverFailed() {
          return !this.hasServerErrors('invoice_delivery_method');
        },
      },
      invoice_delivery_notes: {
        serverFailed() {
          return !this.hasServerErrors('invoice_delivery_notes');
        },
      },
      address: {
        required: requiredIf(function () {
          return this.form.create_location;
        }),
        maxLength: maxLength(255),
        serverFailed() {
          return !this.hasServerErrors('address');
        },
      },
      city: {
        required: requiredIf(function () {
          return this.form.create_location;
        }),
        maxLength: maxLength(255),
        serverFailed() {
          return !this.hasServerErrors('city');
        },
      },
      state: {
        required: requiredIf(function () {
          return this.form.create_location;
        }),
        maxLength: maxLength(255),
        serverFailed() {
          return !this.hasServerErrors('state');
        },
      },
      zip_code: {
        required: requiredIf(function () {
          return this.form.create_location;
        }),
        maxLength: maxLength(255),
        serverFailed() {
          return !this.hasServerErrors('zip_code');
        },
      },
      purchase_order_number: {
        maxLength: maxLength(255),
        serverFailed() {
          return !this.hasServerErrors('purchase_order_number');
        },
      },
      tax_exempt: {},
      invoice_recurring_by_location: {},
      invoice_work_orders_individually: {},
      payment_term_days: {
        required,
        integer,
        minValue: minValue(0),
        serverFailed() {
          return !this.hasServerErrors('payment_term_days');
        },
      },
      netsuite_department_id: {
        serverFailed() {
          return !this.hasServerErrors('netsuite_department_id');
        },
      },
      supply_order_default_markup_percent: {
        decimal,
        between: between(0, 100),
        serverFailed() {
          return !this.hasServerErrors('supply_order_default_markup_percent');
        },
      },
      invoice_format_id: {
        required,
        serverFailed() {
          return !this.hasServerErrors('invoice_format_id');
        },
      },
      technician_payment_gl_code_id: {
        serverFailed() {
          return !this.hasServerErrors('technician_payment_gl_code');
        },
      },
      invoice_gl_code_id: {
        serverFailed() {
          return !this.hasServerErrors('invoice_gl_code');
        },
      },
      supply_order_invoice_gl_code_id: {
        serverFailed() {
          return !this.hasServerErrors('supply_order_invoice_gl_code');
        },
      },
    },
  },
  methods: {
    handleSubmit() {
      if (this.hasFormErrors()) {
        return;
      }
      this.$clientsAPI
        .update(this.value.id, this.form)
        .then((client) => {
          this.resetServerErrors();
          this.$v.$reset();
          this.closeModal();
          this.$emit('input', client);
        })
        .catch((error) => {
          this.processServerErrors(error.response.data.errors);
        });
    },
    onTechnicianPaymentGlCodeChange(glCode) {
      this.form.technician_payment_gl_code_id = glCode ? glCode.id : '';
      this.form.technician_payment_gl_code = glCode;
    },
    onInvoiceGlCodeChange(glCode) {
      this.form.invoice_gl_code_id = glCode ? glCode.id : '';
      this.form.invoice_gl_code = glCode;
    },
    onSupplyOrderInvoiceGlCodeChange(glCode) {
      this.form.supply_order_invoice_gl_code_id = glCode ? glCode.id : '';
      this.form.supply_order_invoice_gl_code = glCode;
    },
    onInvoiceFormatChange(invoiceFormat) {
      this.form.invoice_format_id = invoiceFormat ? invoiceFormat.id : '';
      this.form.invoice_format = invoiceFormat;
    },
    onNetSuiteDepartmentChange(netsuiteDepartment) {
      if (netsuiteDepartment) {
        this.form.netsuite_department_id = Number(netsuiteDepartment.id);
      } else {
        this.form.netsuite_department_id = null;
      }
    },
  },
  watch: {
    'form.supply_order_default_markup_percent': function (newSupplyOrderDefaultMarkupPercent) {
      this.form.supply_order_default_markup = isNaN(newSupplyOrderDefaultMarkupPercent) ? null : newSupplyOrderDefaultMarkupPercent / 100.0;
    },
  },
};
</script>
